import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { ValidationProvider } from '@services/helpers/validation.service';
import { WorkflowDataProvider } from '@services/workflow/workflow.data.service';
import { RandomStringGeneratorProvider } from '@services/helpers/random-string-generator';
import { WorkflowDragNDropProvider } from '@services/workflow/workflow-drag-n-drop';
import { MessageService } from '@services/helpers/message-service/message.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';

import {
	dateItems,
	taskDateSequence,
	automatedActions,
	modalIds,
	taskTypeNames,
	eventTypes
} from '@services/workflow/tasks.model';
import { Subscription } from 'rxjs/Subscription';
import { EmailServiceProvider } from '@services/email/email-service/email.service';

@Injectable()
export class WorkflowProvider {
	taskInfo: any = {};

	constructor(
		private validationService: ValidationProvider,
		private workflowDataService: WorkflowDataProvider,
		private randomStringGenerator: RandomStringGeneratorProvider,
		private workflowDragNDropService: WorkflowDragNDropProvider,
		private messageService: MessageService,
		private timeService: TimeServiceProvider,
		private emailService: EmailServiceProvider
	) {}

	private addDefaultDue(task) {
		const taskCopy = Object.assign({}, task);
		taskCopy.due = 1;
		taskCopy.dateItem = dateItems[0];
		taskCopy.dateSequence = taskDateSequence[0];
		return taskCopy;
	}

	private checkTaskDue(task) {
		const taskCopy = Object.assign({}, task);

		if (!taskCopy.hasDue || !taskCopy.due) {
			delete taskCopy.due;
			delete taskCopy.dateItem;
			taskCopy.dateSequence = null;
		}

		return taskCopy;
	}

	private isEmailTemplateChosen(email, fromSettings) {
		const isEmail =
			email !== null &&
			typeof email !== 'undefined' &&
			typeof email === 'object';

		if (!isEmail) {
			return false;
		}
		if (!fromSettings || isEmail) {
			return true;
		}
		const invalidEmail =
			email.hasOwnProperty('text') &&
			!email.hasOwnProperty('subject') &&
			!email.hasOwnProperty('name');
		return !invalidEmail;
	}

	private isEmailFieldsValid(email, fromSettings): boolean {
		if (typeof email === 'undefined' || email === null) {
			return false;
		}
		const { name, subject } = email;

		if (fromSettings && this.validationService.isStringEmpty(name)) {
			this.messageService.showToaster('e_9', 'emailTemplate', 'name');
			return false;
		}

		if (subject !== null && this.validationService.isStringEmpty(subject)) {
			fromSettings
				? this.messageService.showToaster('e_9', 'emailTemplate', 'subject')
				: this.messageService.showToaster('e_5', 'workflow');
			return false;
		}

		return true;
	}

	private replaceTask(oldTask, newTask, taskTypes) {
		const isTaskId = oldTask.id !== null && oldTask.id !== undefined;
		const identificator = isTaskId ? 'id' : 'number';

		const siblingTasks = this.getSiblingTasks(oldTask, taskTypes);
		siblingTasks.some((item, index) => {
			if (oldTask[identificator] === item[identificator]) {
				siblingTasks[index] = newTask;
				return true;
			}
		});
		return taskTypes;
	}

	private getTaskById(task2Search, taskTypes) {
		const task = Object.assign(task2Search);
		const isTaskId = task.id !== null && task.id !== undefined;

		const identificator = isTaskId ? 'id' : 'tmpId';

		if (!task[identificator]) {
			return false;
		}

		for (let i = 0, max = taskTypes.length; i < max; i++) {
			const taskType = taskTypes[i];
			const foundTask = taskType.find(
				taskInList => task[identificator] === taskInList[identificator]
			);

			if (foundTask) {
				return foundTask;
			}
		}
	}

	private getMainShootIndex(tasksArr): number {
		for (let i = 0, max = tasksArr.length; i <= max; i++) {
			const event = tasksArr[i].hasOwnProperty('eventType')
				? 'eventType'
				: 'calendarEventType';
			if (tasksArr[i][event] === eventTypes.mainShoot) {
				return i;
			}
		}
	}

	private getTaskIndex(task, tasksArr): number {
		for (let i = 0, max = tasksArr.length; i <= max; i++) {
			if (tasksArr[i].number === task.number) {
				return i;
			}
		}
	}

	private concatTasksArr(taskTypes) {
		let tasks = [];
		taskTypes.forEach(taskType => {
			tasks = tasks.concat(...taskType);
		});

		return tasks;
	}

	private filterTasksWithDueDate(tasks) {
		return tasks.filter(task => task.dueDate || task.startDate);
	}

	private addTaskDateTime(tasks) {
		return tasks.map(task => {
			const date = task.dueDate || task.startDate;
			let time = task.dueTime || task.startTime;
			if (task.type === taskTypeNames.lead && task.number === 0) {
				time = null;
			}
			task.dateTime = this.timeService.getDateAndTime(date, time);
			return task;
		});
	}

	private orderTasksByDate(tasks) {
		return tasks.sort(
			(a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
		);
	}

	private getNextTaskByDate(task, taskTypes) {
		let foundTask;

		const tasks = this.concatTasksArr(taskTypes);
		const tasksWithDueDate = this.filterTasksWithDueDate(tasks);
		const tasksWithDateTime = this.addTaskDateTime(tasksWithDueDate);
		const orderedTasksByDate = this.orderTasksByDate(tasksWithDateTime);

		const taskDateTime = this.timeService.getDateAndTime(
			task.startDate,
			task.startTime
		);

		const nextTask = orderedTasksByDate.find(
			taskInList => taskDateTime < taskInList.dateTime
		);

		if (nextTask) {
			foundTask = nextTask;
		} else {
			const lastTaskWithDate =
				orderedTasksByDate[orderedTasksByDate.length - 1];
			foundTask = this.getTaskAfterLastWithDate(taskTypes, lastTaskWithDate);
		}

		return foundTask || tasks[tasks.length - 1];
	}

	private getTaskAfterLastWithDate(taskTypes, lastTaskWithDate) {
		return this.getTaskByNumber(lastTaskWithDate.number + 1, taskTypes);
	}

	private getSiblingTasks(task, taskTypes) {
		const [leadCreated, jobAccepted, jobCompleted] = taskTypes;
		switch (task.type) {
			case taskTypeNames.lead:
				return leadCreated;
			case taskTypeNames.production:
				return jobAccepted;
			case taskTypeNames.postProduction:
				return jobCompleted;
			default:
				break;
		}
	}

	getTaskDates() {
		return dateItems;
	}

	private pluralizeDue(dueAmount, dueItem) {
		let resultDue;
		const isPluralItem = dueItem.endsWith('s');

		if (dueAmount > 1) {
			resultDue = isPluralItem ? dueItem : `${dueItem}s`;
		} else {
			resultDue = isPluralItem ? dueItem.slice(0, -1) : dueItem;
		}

		return resultDue;
	}

	getTaskDateSequence() {
		return taskDateSequence;
	}

	getAutomatedActions() {
		return automatedActions;
	}

	private pasteTaskWithoutDate(task, taskTypes) {
		const [lead, production, postProduction] = taskTypes;
		const mainShootIndex = this.getMainShootIndex(production);

		switch (task.dateSequence) {
			case taskDateSequence[0]:
				lead.splice(lead.length, 0, task);
				break;
			case taskDateSequence[1]:
			case taskDateSequence[2]:
				production.splice(mainShootIndex, 0, task);
				break;
			case taskDateSequence[3]:
				postProduction.splice(0, 0, task);
				break;
			case taskDateSequence[4]:
				postProduction.splice(postProduction.length - 1, 0, task);
				break;
			default:
				postProduction.splice(postProduction.length - 1, 0, task);
				break;
		}

		taskTypes = [lead, production, postProduction];
		taskTypes = this.workflowDragNDropService.renewTaskList(taskTypes);

		return taskTypes;
	}

	private pasteTaskWithDate(task, taskTypes) {
		let taskToPasteType, nextTaskIndex;
		const [lead, production, postProduction] = taskTypes;
		const MIN_INDEX = 1;

		const nextTask = this.getNextTaskByDate(task, taskTypes);
		taskToPasteType = nextTask.type;

		switch (taskToPasteType) {
			case taskTypeNames.lead:
				nextTaskIndex = this.getTaskIndex(nextTask, lead);

				// not to paste on first position
				if (nextTaskIndex === 0) {
					nextTaskIndex = MIN_INDEX;
				}

				lead.splice(nextTaskIndex, 0, task);
				break;

			case taskTypeNames.production:
				nextTaskIndex = this.getTaskIndex(nextTask, production);

				// not to paste on first position
				if (nextTaskIndex === 0) {
					nextTaskIndex = MIN_INDEX;
				}

				production.splice(nextTaskIndex, 0, task);
				break;

			case taskTypeNames.postProduction:
			default:
				nextTaskIndex = this.getTaskIndex(nextTask, postProduction);
				// prefer pasting calendar events in production stage
				if (nextTaskIndex === 0) {
					production.push(task);
					break;
				}
				postProduction.splice(nextTaskIndex, 0, task);
				break;
		}

		taskTypes = [lead, production, postProduction];
		taskTypes = this.workflowDragNDropService.renewTaskList(taskTypes);

		return taskTypes;
	}

	private prepareTaskToSave(task, jobId) {
		const task2save = Object.assign({}, task);

		const { overdueDays, taskEmail: email } = task2save;

		if (overdueDays) {
			task2save.overdueDays = parseInt(overdueDays.match(/\d+/)[0], 10);
		}

		if (email) {
			email.text = this.emailService.addMaiStyling(email.text);
		}

		if (task2save.jobId === null || task2save.jobId === undefined) {
			task2save.jobId = jobId;
		}

		if (task2save.tmpId) {
			delete task2save.tmpId;
		}

		return task2save;
	}

	private deleteTaskFromList(task, taskTypes) {
		const siblingTasks = this.getSiblingTasks(task, taskTypes);

		const isTaskId = task.id !== null && task.id !== undefined;
		const identificator = isTaskId ? 'id' : 'number';

		siblingTasks.some((item, index) => {
			if (task[identificator] === item[identificator]) {
				siblingTasks.splice(index, 1);
				return true;
			}
		});
	}

	setTaskInfo(task: any = {}, taskList: any = [], isForEdit?: boolean) {
		if (!task.due) {
			task = this.addDefaultDue(task);
		} else {
			task.hasDue = true;
		}

		if (task.startDate && task.endDate) {
			task.startDate = this.timeService.createDateAsUTC(task.startDate);
			task.endDate = this.timeService.createDateAsUTC(task.endDate);
		}

		this.taskInfo = { task, taskList, isForEdit };
		return this.taskInfo;
	}

	addEmptyTask(): any {
		return {
			id: null,
			defaultTask: false,
			hasDue: false,
			hasMail: false,
			name: ''
		};
	}

	getTaskInfo() {
		const isTaskInfoEmpty = Object.keys(this.taskInfo).length === 0;
		if (isTaskInfoEmpty) {
			this.taskInfo.task = this.addEmptyTask();
		}
		return this.taskInfo;
	}

	isTaskValid(task, fromSettings = false) {
		const isAutomation = task.hasMail || task.action;

		if (this.validationService.isStringEmpty(task.name)) {
			this.messageService.showToaster('e_1', 'workflow');
			return false;
		}

		if (task.hasDue || task.due) {
			const isDueDaysFilled = task.due || task.due >= 1;
			if (!isDueDaysFilled) {
				this.messageService.showToaster('e_2', 'workflow');
				return false;
			}
		}

		if (isAutomation) {
			if (!task.action) {
				this.messageService.showToaster('e_3', 'workflow');
				return false;
			}

			if (!this.isEmailTemplateChosen(task.taskEmail, fromSettings)) {
				this.messageService.showToaster('e_4', 'workflow');
				return false;
			}

			if (!this.isEmailFieldsValid(task.taskEmail, fromSettings)) {
				return false;
			}
		}

		return true;
	}

	pasteNewTaskInList(taskTypes, task2Paste) {
		let taskTypesCopy = Object.assign(taskTypes);

		const task = this.checkTaskDue(task2Paste);

		let taskInList: any = false;

		if (task.number !== null && task.number !== undefined) {
			if (task.id !== null && task.id !== undefined) {
				taskInList = this.getTaskById(task2Paste, taskTypes);
			} else {
				taskInList = this.getTaskByNumber(task2Paste.number, taskTypes);
			}
		}

		if (taskInList) {
			let currentDateSequence = task.dateSequence;
			if (!currentDateSequence || !task.hasDue || !task.due) {
				currentDateSequence = null;
			}
			const isDateSequenceChanged =
				taskInList.dateSequence !== currentDateSequence;
			const isTaskStartDateChanged = taskInList.startDate !== task.startDate;
			if (isDateSequenceChanged || isTaskStartDateChanged) {
				this.deleteTaskFromList(taskInList, taskTypesCopy);
			} else {
				taskTypesCopy = this.replaceTask(taskInList, task, taskTypesCopy);
				return taskTypesCopy;
			}
		}

		taskTypesCopy = task.startDate
			? this.pasteTaskWithDate(task, taskTypesCopy)
			: this.pasteTaskWithoutDate(task, taskTypesCopy);

		return taskTypesCopy;
	}

	getTaskTemplates(id: number, successCallback): Subscription {
		return this.workflowDataService
			.getTaskTemplates(id)
			.subscribe(
				data => successCallback(data),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	saveWorkflowTemplate(workflow, successCallback): Observable<any> {
		return this.workflowDataService
			.saveWorkflow(workflow)
			.map(
				data => successCallback(data, workflow),
				result => this.errorSaveTask(result.data)
			);
	}

	getJobWorkflow(id, successCallback): Observable<any> {
		return this.workflowDataService
			.getWorkflow(id)
			.map(result => successCallback(result));
	}

	deleteTask(taskTypes, taskId): Observable<any> {
		return this.workflowDataService.deleteTask(taskId);
	}

	validateEmailLinks(action, emailTemplateId) {
		return this.workflowDataService.validateEmailLinks(action, emailTemplateId);
	}

	validateEmailName(email): Observable<any> {
		if (email.id) {
			return Observable.of();
			// return $q.resolve();
		}
		return this.workflowDataService.validateEmailName(email);
	}

	deleteTaskFromSettings(workflow, taskTypes, task) {
		const workflowCopy = JSON.parse(JSON.stringify(workflow));

		if (task.id !== null && task.id !== undefined) {
			if (!workflowCopy.taskToDelete) {
				workflowCopy.taskToDelete = [];
			}
			workflowCopy.taskToDelete.push(task.id);
		}

		this.deleteTaskFromList(task, taskTypes);

		return workflowCopy;
	}

	getTaskByNumber(number: number, taskTypes) {
		for (let i = 0, max = taskTypes.length; i < max; i++) {
			const taskType = taskTypes[i];
			const foundTask = taskType.find(item => number === item.number);
			if (foundTask) {
				return foundTask;
			}
		}
		return null;
	}

	filterTasksBy(taskList, tasksType) {
		return taskList.filter(task => task.type === tasksType);
	}

	calcTaskOverdue(overdueDays: number) {
		let taskOverdue;

		if (overdueDays > 0) {
			taskOverdue = isNaN(overdueDays)
				? NaN
				: `! ${overdueDays} day${overdueDays > 1 ? 's' : ''} overdue`;
		}

		return taskOverdue || overdueDays || null;
	}

	getTaskViewDue(task, taskTypes) {
		let taskViewDue = null;
		const { dateItem, due, dateSequence, previousTask: prevTaskNumber } = task;
		const isTaskDue = [
			dateItem,
			due !== 'undefined',
			due != null,
			dateSequence
		].every(Boolean);
		if (!isTaskDue) {
			return taskViewDue;
		}

		const dateItemTmp = this.pluralizeDue(due, dateItem.toLowerCase());

		const dateSequenceTmp = dateSequence.replace('_', ' ').toLowerCase();

		taskViewDue = `${due} ${dateItemTmp} `;

		if (dateSequence === 'after previous task') {
			const previousTask = this.getTaskByNumber(prevTaskNumber, taskTypes);
			taskViewDue +=
				previousTask !== null ? `after ${previousTask.name}` : dateSequenceTmp;
		} else {
			taskViewDue += dateSequenceTmp;
		}
		return taskViewDue;
	}

	renewTaskViewDue(taskList) {
		const renewedList = Object.assign({}, taskList);
		renewedList.forEach(taskType => {
			taskType.forEach(task => {
				task.taskViewDue = this.getTaskViewDue(task, renewedList);
			});
		});
		return renewedList;
	}

	fillWorkflowTasks(taskList = []) {
		return [
			this.filterTasksBy(taskList, 'LEAD'),
			this.filterTasksBy(taskList, 'PRODUCTION'),
			this.filterTasksBy(taskList, 'POST_PRODUCTION')
		];
	}

	errorSaveTask(data) {
		if (!data || data === 'Task is not valid') {
			return;
		}
		if (data.errorMessage) {
			let errorCode, errorPlace;

			switch (data.errorMessage) {
				case "Tasks can't have due < 1 (if present)":
					errorCode = 'e_2';
					errorPlace = 'workflow';
					break;
				case 'Email text should contain a questionnaire link':
					errorCode = 'e_6';
					errorPlace = 'emailTemplate';
					break;
				case 'Email text should contain a contract link':
					errorCode = 'e_5';
					errorPlace = 'emailTemplate';
					break;
				default:
					errorCode = 'e_0';
					errorPlace = 'general';
			}
			// toastr.clear();
			this.messageService.showToaster(errorCode, errorPlace);
		} else {
			// toastr.clear();
			this.messageService.showToaster('e_0', 'general');
		}
	}

	saveTask(task: any, jobId: number, taskTypes: any): Observable<any> {
		let task2Save = Object.assign({}, task);

		const isTaskId = task2Save.id !== null && task2Save.id !== undefined;

		if (!isTaskId) {
			task2Save.tmpId = this.randomStringGenerator.getMixedString();
		}

		let taskTypesCopy = taskTypes.slice();

		if (!this.isTaskValid(task2Save)) {
			return Observable.throw('Task is not valid');
		}

		task2Save = this.checkTaskDue(task2Save);
		taskTypesCopy = this.pasteNewTaskInList(taskTypesCopy, task2Save);
		taskTypesCopy = this.workflowDragNDropService.renewTaskList(taskTypesCopy);
		const newTaskPosition = this.getTaskById(task2Save, taskTypesCopy);

		task2Save.number = newTaskPosition.number;
		task2Save.type = newTaskPosition.type;

		task2Save = this.prepareTaskToSave(task2Save, jobId);

		return this.workflowDataService.saveTask(task2Save);
	}

	private isInactiveCausedByJobWithoutDate(task, jobDate): boolean {
		return (
			(task.dateSequence === taskDateSequence[2] ||
				task.dateSequence === taskDateSequence[3]) &&
			task.action &&
			!task.completed &&
			!jobDate
		);
	}

	private isInactiveCausedByPrevTask(task, taskList: Array<any>): boolean {
		if (!task || !taskList) {
			return false;
		}

		const prevTask = this.getTaskByNumber(task.number - 1, taskList);
		if (!prevTask) {
			return false;
		}

		const isAfterCalendarEventWithoutDue =
			prevTask.calendarEventType !== null && prevTask.dueDate === null;
		const isAutomationAfterPrev =
			task.action && task.dateSequence === taskDateSequence[4];
		return (
			!task.completed && isAutomationAfterPrev && isAfterCalendarEventWithoutDue
		);
	}

	isLabelShown = (
		label = '',
		task: any = {},
		jobDate?: any,
		taskList?: Array<any>
	) => {
		const eventType = task.eventType || task.calendarEventType;

		switch (label) {
			case 'hasDefaultDue':
				return task.taskDueDefault !== null;
			case 'action-email':
				return (
					task.action === automatedActions[0].shortName &&
					task.taskEmail &&
					!task.due
				);
			case 'action-autoEmail':
				return (
					task.action === automatedActions[0].shortName &&
					task.taskEmail &&
					task.due
				);
			case 'action-contract':
				return task.action === automatedActions[2].shortName;
			case 'action-questionnaire':
				return task.action === automatedActions[1].shortName;
			case 'hasDue':
				return task.taskViewDue !== null;
			case 'hasOverdue':
				return (
					task.overdueDays !== null && task.overdueDays !== 0 && task.hasDue
				);
			case 'noDate':
				return eventType && !jobDate;
			case 'jobAccepted-task':
				return (
					task.type === taskTypeNames.production &&
					task.defaultTask &&
					eventType !== eventTypes.mainShoot
				);
			case 'hasAnyLabel':
				return (
					eventType ||
					task.action ||
					task.taskViewDue !== null ||
					task.taskDueDefault !== null ||
					(task.defaultTask && task.type === taskTypeNames.production)
				);
			case 'isInactive':
				return (
					this.isInactiveCausedByJobWithoutDate(task, jobDate) ||
					this.isInactiveCausedByPrevTask(task, taskList)
				);
			case 'main-shoot':
				return eventType === eventTypes.mainShoot;
			case 'extra-shoot':
				return eventType === eventTypes.extraShoot;
			case 'any-shoot':
				return (
					eventType === eventTypes.extraShoot ||
					eventType === eventTypes.mainShoot
				);
			case 'appointment':
				return eventType === eventTypes.appointment;
			case 'hasType':
				return eventType;
			case 'hasLocation':
				return task.location;
			default:
				console.error('No task label of this type');
				return false;
		}
	};

	chooseModalToShow(task, template) {
		let modal: any = {};

		const eventType = task.eventType || task.calendarEventType;

		if (task.action || task.hasMail) {
			modal = modalIds.automation;
		} else if (eventType === eventTypes.appointment && template) {
			modal = modalIds.appointmentWithoutSchedule;
		} else if (eventType === eventTypes.appointment && !template) {
			modal = modalIds.appointment;
		} else if (eventType === eventTypes.extraShoot && template) {
			modal = modalIds.extraShootWithoutSchedule;
		} else if (eventType === eventTypes.extraShoot && !template) {
			modal = modalIds.extraShoot;
		} else {
			modal = modalIds.todo;
		}

		return modal;
	}

	handleTask(taskToHandle, taskTypes, job) {
		const task = Object.assign({}, taskToHandle);

		if (task.dateItem) {
			task.dateItem = task.dateItem.toLowerCase();
		}

		if (task.due && !task.hasDue) {
			// for old workflows
			task.hasDue = true;
		}

		if (!task.action && task.taskEmail) {
			// for old workflows
			task.action = 'SEND_EMAIL';
		}

		if (task.calendarEventType === 'MAIN_SHOOT') {
			const { startDate, finishDate, startTime, finishTime, location } = job;
			task.location = location;
			task.startDate = startDate;
			task.endDate = finishDate;
			task.startTime = startTime;
			task.endTime = finishTime;
		} else if (task.calendarEvent) {
			const {
				startDate,
				endDate,
				startTime,
				endTime,
				location,
				clientId
			} = task.calendarEvent;
			task.location = location;
			task.startDate = startDate;
			task.endDate = endDate;
			task.startTime = startTime;
			task.endTime = endTime;
			task.sendEmails = clientId ? task.calendarEvent.sendEmails : false;

			if (
				task.startDate &&
				(task.startTime === null || task.finishTime === null)
			) {
				task.allday = true;
			}
		}

		task.overdueDays = this.calcTaskOverdue(task.overdueDays);
		task.taskViewDue = this.getTaskViewDue(task, taskTypes);
		task.taskDueDefault =
			task.defaultTask && task.dueDate ? task.dueDate : null;

		return task;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/workflow/workflow-service/workflow.service.ts